import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { FiLogIn, FiMenu, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAppContext } from "../../../contexts/appContext";
import { MdLightMode, MdDarkMode } from "react-icons/md";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from "@mui/material";
import logo from '../../../assets/app-logo.png';
import ResponsiveContainer from "../../../responsive/components/ResponsiveContainer/ResponsiveContainer";

const Navbar = ({
  navigationItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/blog", label: "Blogs" }
  ]
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { setTheme, theme } = useAppContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setIsOpen(open);
  };

  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <Button
              component={Link}
              to={item.path}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ListItemText primary={item.label} />
            </Button>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2 }}>
        <Button
          component={Link}
          to="/login"
          variant="contained"
          fullWidth
          startIcon={<FiLogIn />}
          onClick={() => setIsOpen(false)}
        >
          Login
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: showNavbar ? 0 : -100 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100 }}
      >
        <AppBar
          position="static"
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1

          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Link to="/">
                <Box
                  component="img"
                  src={logo}
                  alt="App Logo"
                  sx={{
                    height: 30,
                    width: 'auto',
                    mr: 2
                  }}
                />
              </Link>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{ color: 'text.primary' }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Theme Toggle */}
              <IconButton
                onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                sx={{ color: 'primary.dark' }}
              >
                {theme === "dark" ? <MdLightMode /> : <MdDarkMode />}
              </IconButton>

              {/* Login Button - Desktop */}
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                startIcon={<FiLogIn />}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Login
              </Button>

              {/* Mobile Menu Button */}
              <IconButton
                sx={{ display: { md: 'none' }, color: 'text.primary' }}
                onClick={toggleDrawer(true)}
              >
                {isOpen ? <FiX /> : <FiMenu />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </motion.div>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

Navbar.propTypes = {
  logo: PropTypes.string.isRequired,
  navigationItems: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }))
};

export default Navbar;